export default {
  views: {
    welcome: {
      title: "Join the Fractional Executive network",
      description: "Competo connects experienced leaders with companies that need C-level support part time. Your profile is what we match against.",
      points: {
        profile: "Fill in a structured nine step profile - competencies, proven results, availability and pricing.",
        experience: "Add the key companies and functions from your career.",
        documents: "Upload your CV and replace it whenever there is a newer one.",
        privacy: "Only the Competo team sees your profile. Your pricing is never shown to companies."
      },
      note: "You can fill the profile in gradually - save any step and come back later.",
      start_button: "Start my fractional profile",
      dead_end: {
        title: "This portal is not for your account",
        description: "The Fractional Executive portal is for candidates. Your account is not linked to a candidate profile, so there is nothing to edit here. Get in touch if you believe this is a mistake.",
        portal_button: "Open the candidate portal"
      },
      notify: {
        enabled: "Your fractional profile is ready.",
        error: "The profile could not be created."
      }
    },
    experience: {
      title: "Work experience",
      description: "The key companies and functions from your career. The References step lets you highlight the ones that matter most.",
      empty: "Add your first work experience.",
      buttons: {
        add: "Add experience",
        edit: "Edit",
        remove: "Delete",
        cancel: "Cancel"
      },
      table: {
        job_title: "Role",
        company_name: "Company",
        period: "Period",
        current: "present"
      },
      popup: {
        add_title: "New work experience",
        edit_title: "Edit work experience",
        add_button: "Add",
        update_button: "Save",
        fields: {
          job_title: "Role",
          job_title_placeholder: "e.g. Chief Financial Officer",
          company_name: "Company",
          company_name_placeholder: "e.g. Acme Ltd",
          work_start_date: "From",
          work_end_date: "To",
          date_placeholder: "dd.mm.yyyy",
          current_employer: "I still work here",
          experience_in_company: "What you did and achieved",
          experience_in_company_placeholder: "e.g. Led a finance team of five, rebuilt controlling, prepared the business for acquisition."
        }
      },
      prompts: {
        delete: {
          title: "Delete {title}",
          message: "You're about to delete the work experience {title}. Would you like to proceed?",
          cancel: "Cancel",
          confirm: "Delete"
        }
      },
      notify: {
        saved: "Work experience saved.",
        removed: "Work experience deleted.",
        load_error: "Work experience could not be loaded.",
        error: "The work experience could not be saved."
      }
    },
    documents: {
      title: "Documents",
      description: "Your CV is the starting point for any conversation with a company. Upload the latest version.",
      empty: "You have no CV uploaded.",
      upload: "Upload a CV",
      replace: "Replace your CV",
      hint: "PDF and Word (.docx) files are accepted. A new file replaces the current one.",
      dropzone: "<b>Choose a file</b> or drag it in here",
      dropzone_overlay: "Drop the file to upload",
      table: {
        name: "Title"
      },
      buttons: {
        remove: "Delete",
        change: "Replace"
      },
      status: {
        uploading: "Uploading",
        completed: "Uploaded",
        error: "Error"
      },
      prompts: {
        delete: {
          title: "Delete {name}",
          message: "You're about to delete {name}. Would you like to proceed?",
          cancel: "Cancel",
          confirm: "Delete"
        }
      },
      notify: {
        uploaded: "CV uploaded.",
        removed: "File deleted.",
        load_error: "Documents could not be loaded.",
        error: "The file could not be saved."
      },
      proofs: {
        title: "Supporting documents",
        description: "Attach documents that back up your profile - recommendations, evidence of results, certificates.",
        empty: "You have no supporting documents uploaded.",
        upload: "Add a document",
        hint: "PDF, Word and image (JPG, PNG) files up to 10 MB are accepted. Each new file is added to the existing ones.",
        table: {
          name: "Title"
        },
        buttons: {
          remove: "Delete"
        },
        notify: {
          uploaded: "Document uploaded.",
          removed: "File deleted.",
          load_error: "Supporting documents could not be loaded.",
          error: "The file could not be saved."
        }
      }
    },
    profile: {
      title: "Fractional Executive profile",
      description: "Complete your profile so we can match you with the right opportunities. You can update it at any time.",
      eyebrow: "Fractional Executive",
      rail_note: "Fill the profile in at your own pace - the draft saves itself whenever you move to another step.",
      steps_short: {
        basic: "Basic data",
        summary: "Executive summary",
        competences: "Key competencies",
        results: "Proven results",
        fractional_experience: "Fractional experience",
        situations: "Situations and roles",
        availability: "Availability",
        pricing: "Pricing",
        references: "References",
        leadership: "Leadership style"
      },
      steps_nav: "Profile steps",
      progress: "{done} of {total} steps filled in",
      status: {
        title: "Status",
        draft: "Draft",
        submitted: "Submitted for review",
        reviewed: "Reviewed"
      },
      steps_state: {
        filled: "(filled in)",
        empty: "(empty)",
        invalid: "(needs attention)"
      },
      save_state: {
        saving: "Saving ...",
        unsaved: "Unsaved changes",
        saved: "Saved at {time}"
      },
      errors: {
        summary: "Some fields need fixing before the profile can be saved.",
        jump: "Go to step {step}"
      },
      repeater: {
        row: "row {number}"
      },
      prompts: {
        unsaved: {
          title: "Unsaved changes",
          message: "Your changes could not be saved. If you leave this page they will be lost.",
          cancel: "Stay on page",
          confirm: "Leave page"
        },
        remove_row: {
          title: "Remove entry",
          message: "Are you sure you want to remove {name}?",
          cancel: "Cancel",
          confirm: "Remove"
        }
      },
      buttons: {
        save: "Save",
        submit: "Submit for review",
        previous: "Back",
        next: "Next",
        add: "Add new",
        remove: "Remove"
      },
      notify: {
        saved: "Profile saved.",
        submitted: "Profile submitted for review.",
        invalid: "The profile could not be saved - check the highlighted fields.",
        error: "The profile could not be saved."
      },
      steps: {
        basic: "1. Basic data",
        summary: "2. Executive summary",
        competences: "3. Key competencies",
        results: "4. Proven results",
        fractional_experience: "5. Fractional experience",
        situations: "6. Situations and roles",
        availability: "7. Availability and geography",
        pricing: "8. Pricing",
        references: "9. References and credibility",
        leadership: "10. Leadership style"
      },
      basic: {
        description: "Contact details and legal status.",
        name: "Full name",
        name_placeholder: "e.g. Ana Novak",
        email: "Email address",
        email_info: "The address you sign in with. Contact us to change it.",
        phone: "Phone number",
        phone_placeholder: "e.g. +386 41 123 456",
        phone_info: "Include the country code so we can reach you from abroad.",
        address: "Street and number",
        address_placeholder: "e.g. Slovenska cesta 1",
        postal: "Postal code",
        postal_placeholder: "e.g. 1000",
        city: "City",
        city_placeholder: "e.g. Ljubljana",
        country: "Country",
        country_placeholder: "e.g. Slovenia",
        linkedin_url: "LinkedIn URL",
        linkedin_url_placeholder: "https://www.linkedin.com/in/your-name",
        linkedin_url_info: "The full profile URL, not just your handle.",
        position_title: "Job title",
        position_title_placeholder: "e.g. Chief Financial Officer",
        employer: "Current employer",
        employer_placeholder: "e.g. Acme Ltd",
        legal_status: "Legal status",
        legal_status_placeholder: "Select a status",
        legal_status_info: "How you would contract for an engagement.",
        legal_status_options: {
          sp: "Sole trader",
          doo: "Limited company",
          employed: "Employed",
          other: "Other"
        },
        legal_status_note: "Legal status details",
        legal_status_note_placeholder: "e.g. employed, with my employer's consent",
        languages: "Languages",
        languages_hint: "Add the languages you can run meetings in.",
        languages_empty: "Add your first language.",
        language_name: "Language",
        language_name_placeholder: "e.g. English",
        language_level: "Level",
        language_level_placeholder: "Select a level",
        language_levels: {
          a1: "A1 - beginner",
          a2: "A2 - elementary",
          b1: "B1 - intermediate",
          b2: "B2 - upper intermediate",
          c1: "C1 - advanced",
          c2: "C2 - proficient",
          native: "Native"
        }
      },
      summary: {
        description: "Executive summary in 3-5 lines.",
        headline: "Profile headline",
        headline_placeholder: "e.g. Turnaround CFO for mid-sized manufacturers",
        headline_info: "One line covering your role, sector and what sets you apart.",
        career_path: "Key career path",
        career_path_placeholder: "e.g. 15 years in manufacturing finance, from controlling to group CFO of a 300-person business.",
        career_path_info: "Two or three sentences on where you started and where you got to.",
        expertise_areas: "Main areas of expertise",
        expertise_areas_placeholder: "e.g. Refinancing, controlling, due diligence readiness.",
        expertise_areas_info: "List three to five areas you genuinely own.",
        value_creation: "Where you create the most value",
        value_creation_placeholder: "e.g. In the first 100 days I establish financial visibility and a liquidity plan.",
        value_creation_info: "Be concrete: what changes in a business once you join."
      },
      competences: {
        description: "Functional areas, transformation competencies and industry experience.",
        workfields: "A. Functional areas",
        workfields_placeholder: "Select areas",
        workfields_info: "Areas you could take over end to end.",
        industries: "C. Industry experience",
        industries_placeholder: "Select industries",
        industries_info: "Sectors where you know the business, not just the function.",
        transformation: "B. Transformation competencies",
        transformation_hint: "Tick everything you can lead, not just what you have watched up close.",
        transformation_empty: "The competency list is unavailable.",
        transformation_other: "Other transformation competencies",
        transformation_other_placeholder: "e.g. Introducing ESG reporting",
        transformation_other_info: "Separate multiple entries with commas."
      },
      results: {
        description: "Proven results, quantified where possible.",
        results: "Results",
        results_hint: "Three measurable results beat ten general ones.",
        empty: "Add your first result.",
        metric: "Metric",
        metrics: {
          revenue_growth: "Revenue growth",
          ebitda_improvement: "EBITDA improvement",
          cost_reduction: "Cost reduction",
          new_markets: "New market entry",
          org_change: "Organisational change",
          other: "Other"
        },
        value: "Value",
        value_placeholder: "e.g. 18",
        value_info: "The number only - pick the unit in the next field.",
        unit: "Unit",
        units: {
          percent: "%",
          pp: "percentage points",
          eur: "EUR",
          text: "No unit"
        },
        result_description: "Description",
        result_description_placeholder: "e.g. Rebuilding procurement and renegotiating supplier terms cut material costs.",
        company: "Company",
        company_placeholder: "e.g. Acme Ltd",
        period: "Period",
        period_placeholder: "e.g. 2021 - 2023",
        period_info: "The year or range of years the result was achieved in.",
        reference_name: "Reference person (full name)",
        reference_name_placeholder: "e.g. John Smith",
        reference_info: "Someone who can confirm this result. Only add them with their consent.",
        reference_position: "Company and position where you worked together",
        reference_position_placeholder: "e.g. Acme Ltd., CEO",
        reference_contact: "Reference contact",
        reference_contact_placeholder: "e.g. john.smith@acme.com or +386 41 123 456"
      },
      fractional_experience: {
        description: "Your experience working as a Fractional Executive so far, and engagements we can refer to.",
        has_experience: "Have you worked as a Fractional Executive before?",
        has_experience_hint: "Interim and part-time leadership roles at one or several companies at once count too.",
        answers: {
          yes: "Yes",
          no: "No"
        },
        engagements_count: "How many such engagements have you completed?",
        engagements_count_placeholder: "e.g. 3",
        engagements_count_info: "The number of separate engagements with companies, finished or ongoing.",
        note: "About your engagements so far",
        note_placeholder: "e.g. Mostly growth companies of 50 - 200 people, two days a week on average.",
        references: "Reference examples",
        references_hint: "The engagements that best show what you bring to a company. The reference contact is optional - only add it with their consent.",
        references_empty: "Add your first reference example.",
        reference_row: "reference example",
        company: "Company",
        company_placeholder: "e.g. Acme Ltd",
        role: "Role",
        role_placeholder: "e.g. Fractional CFO",
        period: "Period",
        period_placeholder: "e.g. 2022 - 2024",
        reference_description: "Scope and outcome",
        reference_description_placeholder: "e.g. Two days a week; set up controlling and prepared the company for acquisition.",
        contact_name: "Reference person",
        contact_name_placeholder: "e.g. John Smith, CEO",
        contact_info: "Someone who can vouch for the engagement.",
        contact: "Reference contact",
        contact_placeholder: "e.g. john.smith@acme.com"
      },
      situations: {
        description: "Where you add the most value and which roles suit you.",
        situations: "Typical situations",
        situations_hint: "Tick every situation you have led and would take on again.",
        situations_empty: "The situation list is unavailable.",
        situations_other: "Other situations",
        situations_other_placeholder: "e.g. Preparing for an IPO",
        situations_other_info: "For anything not on the list. Separate entries with commas.",
        roles: "Suitable fractional roles",
        roles_hint: "Roles you would take on part time.",
        roles_empty: "The role list is unavailable.",
        roles_other: "Other roles",
        roles_other_placeholder: "e.g. Fractional Chief Data Officer",
        roles_other_info: "For anything not on the list. Separate entries with commas."
      },
      availability: {
        description: "How much time you have available and where you can work.",
        days_per_month: "Days per month",
        days_per_month_placeholder: "e.g. 8",
        days_per_month_info: "Working days you can give to a single company.",
        available_from: "Earliest possible start",
        available_from_placeholder: "dd.mm.yyyy",
        engagement_months_min: "Shortest engagement (months)",
        engagement_months_min_placeholder: "e.g. 3",
        engagement_months_max: "Longest engagement (months)",
        engagement_months_max_placeholder: "e.g. 12",
        engagement_months_info: "In months. Leave empty if you have no limit.",
        note: "Availability note",
        note_placeholder: "e.g. Not available on Tuesdays and Thursdays.",
        scope: "Geographic scope",
        scope_placeholder: "Select a scope",
        scope_info: "How far you are willing to travel regularly.",
        scopes: {
          local: "Local",
          regional: "Regional",
          global: "Global"
        },
        work_modes: "Way of working",
        work_modes_hint: "Tick every arrangement that works for you.",
        modes: {
          onsite: "Onsite",
          hybrid: "Hybrid",
          remote: "Remote"
        },
        regions: "Regions",
        regions_placeholder: "Select regions",
        regions_info: "Regions where you can work onsite.",
        relocate: "Willing to relocate",
        relocate_hint: "Tick if you would move for the right opportunity.",
        geography_note: "Location note",
        geography_note_placeholder: "e.g. I can travel abroad once a month."
      },
      pricing: {
        description: "Pricing is optional and is not shown to companies.",
        daily_rate: "Daily rate",
        daily_rate_placeholder: "e.g. 1200",
        daily_rate_info: "Net rate for one working day, excluding VAT.",
        monthly_retainer: "Monthly retainer",
        monthly_retainer_placeholder: "e.g. 6000",
        monthly_retainer_info: "For an agreed number of days per month, excluding VAT.",
        currency: "Currency",
        currency_placeholder: "EUR",
        currency_info: "Three-letter currency code, e.g. EUR or CHF.",
        negotiable: "Rate is negotiable",
        note: "Pricing note",
        note_placeholder: "e.g. The rate is lower for longer engagements."
      },
      references: {
        description: "Highlight the work experience that matters most, then add supervisory board roles and certificates.",
        experiences: "Work experience",
        experiences_hint: "Add or edit experience right here - it saves immediately, separately from the profile.",
        highlighted: "Highlighted work experience",
        highlighted_hint: "Tick the roles companies should see first.",
        highlighted_empty: "Add a work experience above first.",
        highlighted_current: "present",
        board_roles: "Supervisory board experience",
        board_hint: "Supervisory and management board memberships.",
        board_empty: "Add your first board role.",
        board_row: "board role",
        company: "Company",
        company_placeholder: "e.g. Acme Ltd",
        role: "Role",
        role_placeholder: "e.g. Chair of the supervisory board",
        from: "From",
        to: "To",
        year_placeholder: "e.g. 2019",
        year_info: "Enter the year only, e.g. 2019.",
        to_placeholder: "e.g. 2023",
        to_info: "A year, or \"present\" if you still serve.",
        certificates: "Certificates",
        certificates_hint: "Qualifications that back up your credibility.",
        certificates_empty: "Add your first certificate.",
        certificate_row: "certificate",
        certificate_title: "Certificate name",
        certificate_title_placeholder: "e.g. ACCA",
        issuer: "Issuer",
        issuer_placeholder: "e.g. Slovenian Directors' Association",
        year: "Year"
      },
      leadership: {
        description: "Leadership style and soft factors.",
        styles: "Leadership style",
        styles_hint: "Tick everything that describes you.",
        style_options: {
          hands_on: "Hands-on",
          strategic: "Strategic",
          coaching: "Coaching"
        },
        styles_other: "Other leadership style",
        styles_other_placeholder: "e.g. Servant leadership",
        culture_fit: "Culture fit",
        culture_fit_hint: "The environments where you are most effective.",
        culture_options: {
          corporate: "Corporate",
          entrepreneurial: "Entrepreneurial"
        },
        culture_fit_other: "Other culture fit",
        culture_fit_other_placeholder: "e.g. Family business",
        motivation: "Motivation for fractional work",
        motivation_placeholder: "e.g. I enjoy building finance functions in companies that cannot yet justify a full-time CFO.",
        motivation_info: "A few sentences - clients often read this before the first meeting."
      }
    }
  }
}
